import { useMemo, useState } from "react"

import { useQuery } from "@tanstack/react-query"
import { DateTime } from "luxon"

import { Skeleton } from "@/components/ui/skeleton.tsx"
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table.tsx"
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group.tsx"

import { getFarmBalances } from "../api/getFarmBalances.ts"
import { getFarmBalanceAmounts, sumFarmBalanceAmounts } from "../util/getFarmBalanceAmounts.ts"

import FarmBalanceCells, { PERIOD_COLUMNS } from "./FarmBalanceCells.tsx"

type BalancePeriod = "month" | "quarter"

export default function FarmBalanceTable() {
  const [period, setPeriod] = useState<BalancePeriod>("month")

  const now = DateTime.now()
  const year = now.year
  const month = now.month
  const quarter = now.quarter

  const monthQuery = useQuery({
    queryKey: ["farmBalances", year, "month", month],
    queryFn: () => getFarmBalances(year, month),
    refetchInterval: 30000,
  })

  const quarterQuery = useQuery({
    queryKey: ["farmBalances", year, "quarter", quarter],
    queryFn: () => getFarmBalances(year, undefined, quarter),
    refetchInterval: 30000,
  })

  const { data, isPending, isError } = period === "month" ? monthQuery : quarterQuery

  const rows = useMemo(
    () =>
      [...(data ?? [])]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((b) => ({ id: b.id, name: b.name, amounts: getFarmBalanceAmounts(b) })),
    [data],
  )

  const totals = useMemo(() => sumFarmBalanceAmounts(rows.map((r) => r.amounts)), [rows])

  const periodLabel = period === "month" ? now.toFormat("LLLL yyyy") : `Q${quarter} ${year}`
  const totalColumns = 1 + PERIOD_COLUMNS

  return (
    <div className="flex flex-col gap-2">
      <div className="flex gap-4 items-center">
        <ToggleGroup
          type="single"
          className="shadow-sm border"
          value={period}
          onValueChange={(value: BalancePeriod | "") => value && setPeriod(value)}
        >
          <ToggleGroupItem
            value="month"
            className="p-4 data-[state=on]:bg-muted data-[state=on]:text-foreground"
          >
            Month
          </ToggleGroupItem>
          <ToggleGroupItem
            value="quarter"
            className="p-4 data-[state=on]:bg-muted data-[state=on]:text-foreground"
          >
            Quarter
          </ToggleGroupItem>
        </ToggleGroup>
        <div className="font-semibold">{periodLabel}</div>
      </div>
      <div className="rounded-md border shadow-sm">
        <Table className="table-fixed">
          <colgroup>
            <col className="w-[30%]" />
            {[...Array(PERIOD_COLUMNS).keys()].map((i) => (
              <col key={i} className="w-[14%]" />
            ))}
          </colgroup>
          <TableHeader className="bg-accent">
            <TableRow>
              <TableHead className="px-4">Farm</TableHead>
              <TableHead className="text-center">Manure In</TableHead>
              <TableHead className="text-center">Effluent Out</TableHead>
              <TableHead className="text-center">Balance</TableHead>
              <TableHead className="text-center whitespace-normal">Penalty Volume</TableHead>
              <TableHead className="text-center whitespace-normal">Penalty/Royalty</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isError ? (
              <TableRow>
                <TableCell colSpan={totalColumns} className="text-center text-status-bad py-6">
                  Failed to load farm balances.
                </TableCell>
              </TableRow>
            ) : isPending ? (
              [...Array(8).keys()].map((i) => (
                <TableRow key={i}>
                  {[...Array(totalColumns).keys()].map((j) => (
                    <TableCell key={j} className="px-4 py-4">
                      <Skeleton className="h-5 w-full" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={totalColumns}
                  className="text-center text-muted-foreground py-6"
                >
                  No farm balances found.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell className="px-4 py-4 truncate" title={row.name}>
                    {row.name}
                  </TableCell>
                  <FarmBalanceCells amounts={row.amounts} />
                </TableRow>
              ))
            )}
          </TableBody>
          {!isError && !isPending && rows.length > 0 && (
            <TableFooter>
              <TableRow>
                <TableCell className="px-4 py-4 font-semibold">Total</TableCell>
                <FarmBalanceCells amounts={totals} />
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </div>
    </div>
  )
}
